<template>
  <div>
    <div class="container-fluid p-2 p-md-4">

      <ConcessionaireHeader :current-concessionaire-type="currentConcessionaireType" :total-registers="totalRegisters"
        :total-without-administrators="totalWithoutAdministrators" :total-without-users="totalWithoutusers"
        :without-users="withoutUsers" :without-administrators="withoutAdministrators"
        @update:withoutUsers="(newValue) => (withoutUsers = newValue)"
        @update:withoutAdministrators="(newValue) => (withoutAdministrators = newValue)" />

      <div class="d-flex justify-content-end align-items-center mb-3 gap-2">
        <button @click="handleExportConcessionaires" class="btn btn-outline-primary transition-all"
          :disabled="loadingExport">
          <i v-if="loadingExport" class="fas fa-spinner fa-spin me-2"></i>
          <i v-else class="fas fa-file-export me-2"></i>
          {{ exportButtonLabel }}
        </button>

        <button v-if="currentConcessionaireTypeValue === 1" @click="modalNew" class="btn btn-primary add-button">
          AGREGAR NUEVO <i class="fas fa-plus ml-2"></i>
        </button>
      </div>

      <div class="mb-3">
        <ConcessionaireDropdownFilters :selected-province="selectedProvince" :provinces-options="provincesOptions"
          :loading-provinces="loadingProvinces" @update:selected-province="selectedProvince = $event"
          :selected-locality="selectedLocality" :localities-options="localitiesOptions"
          :loading-localities="loadingLocalities" @update:selected-locality="selectedLocality = $event"
          :selected-regional="selectedRegional" :regionals-options="regionalsOptions"
          :loading-regionals="loadingRegionals" @update:selected-regional="selectedRegional = $event"
          :selected-brand="selectedBrand" :brands-options="brandsOptions" :loading-brands="loadingBrands"
          @update:selected-brand="selectedBrand = $event" :selected-economic-group="selectedEconomicGroup"
          :economic-groups-options="economicGroupsOptions" :loading-economic-groups="loadingEconomicGroups"
          @update:selected-economic-group="selectedEconomicGroup = $event" @reset-filters="resetFilters" />
      </div>

      <div class="search-bar mb-3">
        <div class="input-group">
          <span class="input-group-text bg-white border-end-0">
            <i class="fas fa-search text-muted"></i>
          </span>
          <input type="text" v-model="searchTerm" class="form-control border-start-0 ps-0"
            placeholder="Buscar por CUIT, Razón Social, etc..." />
        </div>
      </div>

      <div class="pagination-info mb-2 text-muted fst-italic">
        <small>Página {{ currentPage }} de {{ lastPage }}</small>
      </div>

      <div class="table-responsive bg-white shadow-sm rounded border">
        <table ref="table" class="table table-striped table-hover table-formal align-middle text-nowrap mb-0">
          <thead class="bg-light">
            <tr>
              <th>CUIT</th>
              <th>Razón Social</th>
              <th>Grupo Económico</th>
              <th>Provincia</th>
              <th>Localidad</th>
              <th>Regional</th>
              <th>Calle</th>
              <th>Rubro</th>
              <th>Marca</th>
              <th>Estado</th>
              <th class="sticky-col bg-light" style="width: 50px;"></th>
            </tr>
          </thead>
          <tbody>

            <TableSpinner v-if="loading" :colspan="11" text="Cargando concesionarias..." />

            <tr v-else-if="!loading && concessionaires.length === 0">
              <td colspan="11" class="text-center py-5 text-muted">
                <i class="fas fa-inbox fa-3x mb-3 d-block opacity-25"></i>
                <span class="fw-medium">No se encontraron resultados.</span>
              </td>
            </tr>

            <template v-else>
              <tr v-for="concessionaire in concessionaires" :key="concessionaire.id">
                <td>{{ concessionaire.cuit || "N/A" }}</td>
                <td class="fw-medium">
                  <template v-if="concessionaire.type !== 1 && concessionaire.central_house">
                    {{ concessionaire.central_house.business_name }} - {{ concessionaire.business_name }}
                  </template>
                  <template v-else>
                    {{ concessionaire.business_name || "Nombre no disponible" }}
                  </template>
                </td>
                <td>
                  <div v-if="concessionaire.economic_group">
                    <span class="d-inline-block text-truncate"
                      style="max-width: 150px; cursor: help; text-decoration: underline dotted #999;"
                      :title="`CUIT: ${concessionaire.economic_group.cuit || 'N/A'}\nEmail: ${concessionaire.economic_group.email || 'N/A'}`">
                      {{ concessionaire.economic_group.name }}
                    </span>
                  </div>
                  <span v-else class="text-muted small">-</span>
                </td>
                <td>{{ concessionaire.province_name || "-" }}</td>
                <td>{{ concessionaire.locality_name || "-" }}</td>
                <td>{{ concessionaire.regional_name || "-" }}</td>
                <td>{{ concessionaire.street_name || "-" }}</td>
                <td><span class="badge bg-light text-dark border">{{ getVehicleTypeName(concessionaire.sector)
                    }}</span>
                </td>
                <td>{{ getBrandNames(concessionaire.brands) }}</td>
                <td>
                  <span
                    :class="concessionaire.status === 1 ? 'badge bg-success-subtle text-success' : 'badge bg-danger-subtle text-danger'">
                    {{ concessionaire.status === 1 ? "Habilitado" : "Inhabilitado" }}
                  </span>
                </td>

                <td class="sticky-col" :class="{ 'active-sticky-col': dropdownVisible === concessionaire.id }">
                  <div class="dropdown d-flex justify-content-center w-100">

                    <button class="btn btn-link p-0 btn-action text-dark" type="button"
                      @click.stop="toggleDropdown(concessionaire.id, $event)">
                      <i class="fas fa-cog"></i>
                    </button>

                    <Teleport to="body">
                      <ul v-if="dropdownVisible === concessionaire.id" class="dropdown-menu show shadow fixed-menu"
                        :class="dropdownDirection" :style="dropdownStyle">
                        <li>
                          <a class="dropdown-item" href="#" @click.prevent="editConcessionaire(concessionaire)">
                            <i class="fas fa-edit me-2 text-primary"></i> Editar
                          </a>
                        </li>
                        <li v-if="concessionaire.type !== 2">
                          <a class="dropdown-item" href="#" @click.prevent="addBranch(concessionaire)">
                            <i class="fas fa-plus me-2 text-secondary"></i> Agregar Sucursal
                          </a>
                        </li>

                        <li>
                          <hr class="dropdown-divider">
                        </li>

                        <li>
                          <a class="dropdown-item" :class="concessionaire.status === 1 ? 'text-danger' : 'text-success'"
                            href="#" @click.prevent="disableConcessionaireHandler(concessionaire)">
                            <i
                              :class="['me-2', concessionaire.status === 1 ? 'fas fa-ban' : 'fas fa-check-circle']"></i>
                            {{ concessionaire.status === 1 ? "Inhabilitar" : "Habilitar" }}
                          </a>
                        </li>

                        <li>
                          <hr class="dropdown-divider">
                        </li>

                        <li>
                          <a class="dropdown-item" href="#" @click.prevent="viewUsers(concessionaire)">
                            <i class="fas fa-users me-2 text-info"></i> Ver usuarios
                          </a>
                        </li>
                        <li v-if="concessionaire.type !== 2">
                          <a class="dropdown-item" href="#" @click.prevent="viewBranch(concessionaire)">
                            <i class="fas fa-folder-open me-2 text-warning"></i> Ver sucursales
                          </a>
                        </li>
                        <li>
                          <a class="dropdown-item" href="#" @click.prevent="viewMoreInfo(concessionaire)">
                            <i class="fas fa-info-circle me-2 text-muted"></i> Más información
                          </a>
                        </li>
                      </ul>
                    </Teleport>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <div class="pagination d-flex flex-wrap justify-content-center justify-content-md-end mt-3 gap-2">
        <button @click="loadConcessionaires(1)" class="btn btn-sm btn-outline-secondary"
          :disabled="currentPage === 1 || loading">
          <i class="fas fa-angle-double-left"></i> <span class="d-none d-sm-inline">Primera</span>
        </button>
        <button @click="loadConcessionaires(currentPage - 1)" class="btn btn-sm btn-outline-secondary"
          :disabled="currentPage === 1 || loading">
          <i class="fas fa-angle-left"></i> Anterior
        </button>
        <button @click="loadConcessionaires(currentPage + 1)" class="btn btn-sm btn-outline-secondary"
          :disabled="!hasMorePages || loading">
          Siguiente <i class="fas fa-angle-right"></i>
        </button>
        <button @click="loadConcessionaires(lastPage)" class="btn btn-sm btn-outline-secondary"
          :disabled="currentPage === lastPage || loading">
          <span class="d-none d-sm-inline">Última</span> <i class="fas fa-angle-double-right"></i>
        </button>
      </div>

    </div>

    <SucursalSelector :isOpen="modalOpen" :sucursals="sucursals" :showSelection="showSelection"
      @close="modalOpen = false" @success="handleModalSuccess" />

    <ConcessionaireUsersModal :isOpen="showUsersModal" :concessionaire="selectedConcessionaire"
      @close="showUsersModal = false" />

    <ConcessionaireDetailsModal v-if="showDetailsModal" :concessionaire="selectedConcessionaireDetails"
      @close="showDetailsModal = false" />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useStore } from "vuex";
import Swal from "sweetalert2";
import { getConcessionaireTypeLabel, getConcessionaireTypeValue } from "@/constants/concessionaireTypes.js";
import {
  changeCentralHouseStatus,
  getSucursals,
  exportConcessionairesFiltered
} from "@/services/api/concessionaireService.js";
import SucursalSelector from "./components/SucursalSelector.vue";
import ConcessionaireUsersModal from "./components/ConcessionaireUsersModal.vue";
import ConcessionaireDetailsModal from "./components/ConcessionaireDetailsModal.vue";
import ConcessionaireHeader from "./components/ConcessionaireHeader.vue";
import ConcessionaireDropdownFilters from "./components/filters/ConcessionaireDropdownFilters.vue";
import TableSpinner from "@/components/ui/TableSpinner.vue";

import { useConcessionaire } from "@/composables/concessionaire/useConcessionaire";
import { useConcessionaireDisabling } from "@/composables/concessionaire/useConcessionaireDisabling";
import { useVehicleTypes } from "@/composables/useVehicleTypes";
import { useConcessionaireFilters } from "@/composables/concessionaire/useConcessionaireFilters";
import { useSmartDropdown } from "@/composables/useSmartDropdown";

export default {
  name: "ConcesionarioView",
  components: {
    SucursalSelector,
    ConcessionaireUsersModal,
    ConcessionaireDetailsModal,
    ConcessionaireHeader,
    ConcessionaireDropdownFilters,
    TableSpinner,
  },
  setup() {
    const showDetailsModal = ref(false);
    const selectedConcessionaireDetails = ref(null);
    const showUsersModal = ref(false);
    const selectedConcessionaire = ref(null);
    const selectedSucursal = ref(null);
    const modalOpen = ref(false);
    const showSelection = ref(true);

    const router = useRouter();
    const route = useRoute();
    const store = useStore();

    const { getVehicleTypeName } = useVehicleTypes();

    const perPage = ref(15);
    const searchTerm = ref("");

    const {
      dropdownVisible,
      dropdownDirection,
      toggleDropdown,
      closeDropdown,
      dropdownStyle
    } = useSmartDropdown();

    const loadingExport = ref(false);

    const {
      selectedProvince,
      selectedLocality,
      selectedRegional,
      selectedBrand,
      selectedSector,
      provincesOptions,
      localitiesOptions,
      regionalsOptions,
      brandsOptions,
      sectorsOptions,
      resetFilters,
      activeFilters,
      loadFilterOptions,
      loadingProvinces,
      loadingLocalities,
      loadingRegionals,
      loadingBrands,
      withoutUsers,
      withoutAdministrators,
      selectedEconomicGroup,
      economicGroupsOptions,
      loadingEconomicGroups,
    } = useConcessionaireFilters();

    const exportButtonLabel = computed(() => {
      return currentConcessionaireTypeValue.value === 2
        ? "Exportar Sucursales"
        : "Exportar Concesionarios";
    });

    const handleExportConcessionaires = async () => {
      if (loadingExport.value) return;

      loadingExport.value = true;

      Swal.fire({
        title: "Generando archivo",
        html: "Por favor espera mientras preparamos la exportación...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const filters = activeFilters.value;

        const response = await exportConcessionairesFiltered({
          search: searchTerm.value,
          type: currentConcessionaireTypeValue.value,

          province_id: filters.province_id,
          locality_id: filters.locality_id,
          regional_id: filters.regional_id,
          sector: filters.sector,
          brand_id: filters.brand_id,
          economic_group_id: filters.economic_group_id,

          withoutUsers: filters.withoutUsers,
          withoutAdministrators: filters.withoutAdministrators,
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;

        const filename =
          currentConcessionaireTypeValue.value === 2
            ? "sucursales_exportadas.csv"
            : "concesionarios_exportados.csv";

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();

        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);

        await Swal.fire({
          icon: "success",
          title: "Exportación completada",
          text: "El archivo se ha descargado correctamente",
        });
      } catch (error) {
        console.error("Error al exportar concesionarios/sucursales:", error);

        Swal.fire({
          icon: "error",
          title: "Error en la exportación",
          text:
            error.response?.data?.message || "No se pudo completar la exportación",
        });
      } finally {
        loadingExport.value = false;
        Swal.close();
      }
    };

    const {
      concessionaires,
      loading,
      error,
      currentPage,
      lastPage,
      hasMorePages,
      totalRegisters,
      totalWithoutAdministrators,
      totalWithoutusers,
      fetchConcessionaires,
    } = useConcessionaire();

    const getBrandNames = (brands) => {
      if (!brands || brands.length === 0) return "N/A";
      return brands.map((brand) => brand.brand_name).join(", ");
    };

    const { sucursals, concessionaireId, disableConcessionaire } = useConcessionaireDisabling();

    const disableConcessionaireHandler = async (concessionaire) => {
      showSelection.value = true;
      await disableConcessionaire(concessionaire, loadConcessionaires, openModal);
      closeDropdown();
    };

    const viewUsers = (concessionaire) => {
      if (!concessionaire) return;
      selectedConcessionaire.value = {
        business_name: concessionaire.business_name || "Sin nombre",
        administrators: Array.isArray(concessionaire.administrators) ? [...concessionaire.administrators] : [],
        users: Array.isArray(concessionaire.users) ? [...concessionaire.users] : [],
      };
      showUsersModal.value = true;
      closeDropdown();
    };

    const viewMoreInfo = (concessionaire) => {
      selectedConcessionaireDetails.value = concessionaire;
      showDetailsModal.value = true;
      closeDropdown();
    };

    const viewBranch = async (concessionaire) => {
      try {
        const response = await getSucursals(concessionaire.id);
        sucursals.value = response.data || [];
        if (sucursals.value.length === 0) {
          Swal.fire({
            icon: "info",
            title: "Sin Sucursales",
            text: `El concesionario ${concessionaire.business_name} no tiene sucursales registradas.`,
          });
          return;
        }
        modalOpen.value = true;
        showSelection.value = false;
        closeDropdown();
      } catch (error) {
        console.error("Error al obtener las sucursales:", error);
        Swal.fire({ icon: "error", title: "Error", text: "Error al obtener sucursales." });
      }
    };

    const currentConcessionaireType = computed(() => {
      const subMenu = route.params.subMenu;
      const typeValue = getConcessionaireTypeValue(subMenu);
      return getConcessionaireTypeLabel(typeValue);
    });

    const currentConcessionaireTypeValue = computed(() => {
      const subMenu = route.params.subMenu;
      return getConcessionaireTypeValue(subMenu);
    });

    const loadConcessionaires = async (page = 1) => {
      await fetchConcessionaires({
        subMenuLabel: route.params.subMenu,
        page,
        perPage: perPage.value,
        search: searchTerm.value,
        ...activeFilters.value,
      });
    };

    const modalNew = () => {
      router.push({ name: "ConcessionaireForm" });
    };

    const editConcessionaire = (concessionaire) => {
      store.dispatch("updateHabitualistaData", concessionaire);
      router.push({
        name: "ConcessionaireFormStepTwo",
        query: { isEditing: "true", type: currentConcessionaireTypeValue.value },
      });
      closeDropdown();
    };

    const addBranch = async (concessionaire) => {
      store.dispatch("updateHabitualistaData", concessionaire);
      router.push({
        name: "ConcessionaireFormStepTwo",
        query: { isEditing: "true", isAddBranch: "true" },
      });
      closeDropdown();
    };

    const openModal = () => {
      modalOpen.value = true;
    };

    const handleModalSuccess = ({ id, status }) => {
      if (!id) return;
      selectedSucursal.value = { id, status };
      modalOpen.value = false;
      processSelectedSucursal();
    };

    const processSelectedSucursal = async () => {
      if (!selectedSucursal.value) return;
      const { id, status } = selectedSucursal.value;
      try {
        const response = await changeCentralHouseStatus(concessionaireId.value, { new_central_house_id: id, status });
        showSuccessNotification(response.message || "Cambio realizado con éxito");
        loadConcessionaires();
      } catch (error) {
        showNotification("error", "Error", "No se pudo cambiar el estado de la casa central.");
      }
    };

    const showSuccessNotification = (message) => showNotification("success", "¡Éxito!", message);
    const showErrorNotification = (message) => showNotification("error", "¡Error!", message);
    const showNotification = (type, title, message) => {
      Swal.fire({
        icon: type,
        title,
        text: message,
        confirmButtonText: "Entendido",
        confirmButtonColor: type === "success" ? "#007BFF" : "#d33",
      });
    };

    onMounted(() => {
      loadFilterOptions();
    });

    watch(
      [
        selectedProvince,
        selectedLocality,
        selectedRegional,
        selectedSector,
        selectedBrand,
        searchTerm,
        () => currentConcessionaireTypeValue.value,
        withoutUsers,
        withoutAdministrators,
        selectedEconomicGroup,
      ],
      (newValues, oldValues) => {
        const isConcessionaireTypeChanged = newValues[6] !== oldValues[6];

        loadConcessionaires(1);

        if (isConcessionaireTypeChanged) {
          resetFilters();
        }
      },
      { immediate: true }
    );

    return {
      concessionaires,
      loading,
      error,
      currentPage,
      lastPage,
      hasMorePages,
      totalRegisters,
      totalWithoutAdministrators,
      totalWithoutusers,

      selectedSector,
      selectedProvince,
      selectedLocality,
      selectedRegional,
      selectedBrand,
      selectedEconomicGroup,
      withoutUsers,
      withoutAdministrators,

      sectorsOptions,
      provincesOptions,
      localitiesOptions,
      regionalsOptions,
      brandsOptions,
      economicGroupsOptions,

      loadingProvinces,
      loadingLocalities,
      loadingRegionals,
      loadingBrands,
      loadingEconomicGroups,

      // Métodos de lógica
      resetFilters,
      fetchConcessionaires,
      loadConcessionaires,

      // UI Inputs
      searchTerm,
      perPage,

      // Dropdown
      dropdownVisible,
      dropdownDirection,
      toggleDropdown,

      // Modales y Acciones
      showDetailsModal,
      selectedConcessionaireDetails,
      viewMoreInfo,
      showUsersModal,
      selectedConcessionaire,
      viewUsers,
      showSelection,
      viewBranch,
      disableConcessionaireHandler,
      showNotification,
      showErrorNotification,
      showSuccessNotification,
      selectedSucursal,
      handleModalSuccess,
      modalOpen,
      sucursals,
      modalNew,
      editConcessionaire,
      addBranch,

      // Utils
      getVehicleTypeName,
      getBrandNames,
      currentConcessionaireTypeValue,
      currentConcessionaireType,
      getConcessionaireTypeLabel,

      // Exportación
      loadingExport,
      exportButtonLabel,
      handleExportConcessionaires,
      dropdownStyle,
    };
  },
};
</script>

<style scoped>
.status-enabled {
  color: green;
  font-weight: bold;
}

.status-disabled {
  color: red;
  font-weight: bold;
}

.add-button {
  font-size: 0.875rem;
  padding: 8px 10px;
  transition: transform 0.1s ease, background-color 0.3s;
}

.add-button:active {
  transform: scale(0.95);
  background-color: #003f7f;
}



.table-responsive {
  position: relative;
  overflow: auto;
  min-height: 250px;
}

.table-formal {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.85rem;
  color: #333;
}

.btn-action {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: #6c757d !important;
}

.btn-action:hover,
.dropdown.show .btn-action {
  background-color: #e9ecef;
  color: #0d6efd !important;
}

.sticky-col {
  background-color: #fff;
  position: static;
  right: auto;
  z-index: 1;
  border-left: 1px solid #dee2e6;
}

@media (min-width: 1200px) {
  .sticky-col {
    position: sticky;
    right: 0;
    z-index: 5;
    box-shadow: -5px 0 10px -5px rgba(0, 0, 0, 0.1);
    background-color: #fff !important;
    border-left: none;
  }

  thead .sticky-col {
    z-index: 10;
    background-color: #f8f9fa !important;
  }
}

.dropdown .dropdown-menu {
  position: absolute;
  z-index: 2000;
  min-width: 170px;
  padding: 0.5rem 0;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  background-color: #fff;
  text-align: left;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  right: 100% !important;
  left: auto !important;
  margin-right: 0.25rem;
  top: 0;
}

.dropdown-item {
  padding: 8px 16px;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
}

.dropdown-item i {
  width: 20px;
  text-align: center;
  margin-right: 8px;
}

.dropdown-item:hover {
  background-color: #f1f3f5;
}

@media (max-width: 768px) {

  .table-formal th,
  .table-formal td {
    padding: 8px 6px;
    font-size: 0.75rem;
  }
}

.dropup .dropdown-menu {
  top: auto !important;
  bottom: 100% !important;
  margin-bottom: 0.25rem;
  right: 100% !important;
  left: auto !important;
  margin-right: 0.25rem;
}

.active-sticky-col {
  z-index: 1000 !important;
  background-color: #e9ecef !important;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.1), -5px 0 10px -5px rgba(0, 0, 0, 0.2) !important;
}

.active-sticky-col .btn-action {
  color: #0d6efd !important;
  background-color: transparent;
}


.fixed-menu {
  margin: 0;
  transform: translateX(-100%);
}

.fixed-menu.dropstart {
  transform: translate(calc(-100% - 5px), 0);
}

.fixed-menu.dropup {
  transform: translate(calc(-100% - 5px), -100%);
}
</style>